<template>
  <div class="text-xs">
    <LoadingIndicator v-if="loading" message="Loading feeds..." />

    <div v-else-if="error" class="text-red-600">
      Couldn't load feeds.
      <button type="button" class="underline ml-1" @click="load">Retry</button>
    </div>

    <div v-else-if="!rows.length" class="text-gray-500">
      No feeds contributed to identity for this tenant.
    </div>

    <template v-else>
      <!-- This answers "what reached IDENTITY", which is NOT the same question
           as the Feeds tab (did the file land). A feed can be delivering
           perfectly and contribute nothing here because its patients did not
           resolve — that gap is the reason this view exists. -->
      <div class="text-gray-500 mb-2">
        {{ rows.length }} feeds contributing to identity ·
        {{ fmt(totalPatients) }} people ·
        {{ fmt(totalRecords) }} source records.
        Freshness is what the feed says about itself (last report date), not our
        load time.
      </div>

      <div class="border border-gray-200 rounded overflow-auto max-h-[600px]">
        <table class="w-full">
          <colgroup>
            <col style="width: 22%" /><col style="width: 6%" />
            <col style="width: 11%" /><col style="width: 11%" />
            <col style="width: 11%" /><col style="width: 8%" />
            <col style="width: 11%" /><col style="width: 8%" />
            <col style="width: 12%" />
          </colgroup>
          <thead class="sticky top-0 bg-white shadow-sm">
            <tr class="text-left font-medium uppercase text-gray-500 border-b border-gray-200">
              <th class="py-2 pl-3 pr-2">Feed</th>
              <th class="py-2 px-2 text-right" title="SOURCE_RELIABILITY — lower wins">Rel</th>
              <th class="py-2 px-2 text-right">Records</th>
              <th class="py-2 px-2 text-right">People</th>
              <th class="py-2 px-2 text-right">ID facts</th>
              <th class="py-2 px-2 text-right">ID types</th>
              <th class="py-2 px-2 text-right">Assign facts</th>
              <th class="py-2 px-2 text-right">Contracts</th>
              <th class="py-2 px-2 pr-3">Last report</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.SOURCE" class="border-b border-gray-50 hover:bg-gray-50">
              <td class="py-1.5 pl-3 pr-2 align-middle">{{ r.SOURCE }}</td>
              <td class="py-1.5 px-2 align-middle text-right">
                <span
                  v-if="r.SOURCE_RELIABILITY != null"
                  :class="r.SOURCE_RELIABILITY <= 2 ? 'text-gray-900 font-medium' : 'text-gray-500'"
                >{{ r.SOURCE_RELIABILITY }}</span>
                <span v-else class="text-gray-300">—</span>
              </td>
              <td class="py-1.5 px-2 align-middle text-right text-gray-600">{{ fmt(r.RECORDS) }}</td>
              <td class="py-1.5 px-2 align-middle text-right font-medium">{{ fmt(r.PATIENTS) }}</td>
              <td class="py-1.5 px-2 align-middle text-right text-gray-600">{{ fmt(r.ID_ASSERTIONS) }}</td>
              <td class="py-1.5 px-2 align-middle text-right text-gray-600">{{ fmt(r.ID_TYPES) }}</td>
              <td class="py-1.5 px-2 align-middle text-right text-gray-600">{{ fmt(r.ASSIGN_ASSERTIONS) }}</td>
              <td class="py-1.5 px-2 align-middle text-right text-gray-600">{{ fmt(r.CONTRACTS) }}</td>
              <td
                class="py-1.5 px-2 pr-3 align-middle"
                :class="staleClass(r.LAST_REPORT_DATE)"
              >{{ r.LAST_REPORT_DATE || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="text-gray-500 mt-2">
        Reliability: 0 manual · 1 customer roster · 2 CMS issuer-direct ·
        3 payer direct · 4 derived · 5 EHR. Lower wins when feeds disagree —
        this is what IS_PRIMARY means.
      </div>
    </template>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { getFeedCoverage } from '@/services/api/mdr/read';

// A feed quieter than this is worth a second look. Not an alert threshold —
// several sources here are legitimately annual or one-off, so this shades the
// cell rather than claiming anything is broken.
const STALE_DAYS = 45;

export default {
  name: 'MDRFeeds',
  components: { LoadingIndicator },
  data() {
    return { rows: [], loading: false, error: false };
  },
  computed: {
    totalPatients() {
      // NOT a patient count for the tenant — one person appears under several
      // feeds, so this deliberately sums contributions, not people.
      return this.rows.reduce((a, r) => a + (Number(r.PATIENTS) || 0), 0);
    },
    totalRecords() {
      return this.rows.reduce((a, r) => a + (Number(r.RECORDS) || 0), 0);
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    fmt(n) {
      return n == null ? '—' : Number(n).toLocaleString();
    },
    staleClass(d) {
      if (!d) return 'text-gray-300';
      const days = (Date.now() - new Date(d).getTime()) / 86400000;
      return days > STALE_DAYS ? 'text-amber-700' : 'text-gray-600';
    },
    async load() {
      this.loading = true;
      this.error = false;
      try {
        this.rows = await getFeedCoverage();
      } catch {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
